import {
  Text,
  Button,
  Card,
  Container,
  Grid,
  Loading,
  Badge,
  Spacer,
} from "@nextui-org/react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { TbArrowBackUp, TbPrinter } from "react-icons/tb";
import { database } from "../firebase";
import { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
  formatSecondHandProduct,
  inferSecondHandCategory,
  secondHandInventoryKey,
} from "../utils/secondHandProduct";

const SecondHandInventory = () => {
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({
    content: () => contentRef.current,
    removeAfterPrint: false,
  });

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      let items = [];

      const querySnapshot = await database
        .collection("second_hand_docs")
        .where("status", "==", "pending")
        .orderBy("phone", "asc")
        .get();

      querySnapshot.forEach((doc) => items.push(doc.data()));

      let counts = {};
      items.forEach((doc) => {
        const phoneName = secondHandInventoryKey(doc);

        if (!counts[phoneName]) {
          counts[phoneName] = {
            category: inferSecondHandCategory(doc),
            name: formatSecondHandProduct(doc),
            qty: 0,
            totalPurchase: 0,
          };
        }

        counts[phoneName].qty += 1;
        counts[phoneName].totalPurchase += doc.purchaseAmount;
      });

      setData(counts);
    } catch (err) {
      console.log(err);
      setData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div className="header">
        <Link to="/analytics" className="headerLink">
          <TbArrowBackUp className="icoAdjust" />
        </Link>
        <Text b>2H Inventory</Text>
        <div className="headerLink headerLinkAddon">
          {/* Keeping it simple for now, can add Export later if needed */}
        </div>
      </motion.div>
      <div className="main_container">
        {/* Header matching Inventory.js */}

        <Container sm gap={0} css={{ pb: "$10" }}>
          {loading ? (
            <Container
              css={{
                height: "50vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loading type="points" />
            </Container>
          ) : (
            Object.keys(data).length > 0 && (
              <>
                <Container
                  display="flex"
                  justify="center"
                  css={{ pt: 67, mb: "$8" }}
                >
                  <Button
                    icon={<TbPrinter />}
                    onClick={reactToPrintFn}
                    auto
                    rounded
                    color="primary"
                  >
                    Print
                  </Button>
                </Container>

                <Grid.Container gap={0} css={{ px: "$4", pb: "$8" }}>
                  {Object.keys(data)
                    .sort((a, b) => data[b].qty - data[a].qty)
                    .map((doc, i) => {
                      const itemDoc = data[doc];

                      return (
                        <Grid xs={12} key={i} css={{ mb: "$4" }}>
                          <Card
                            variant="bordered"
                            css={{
                              p: "8px 12px",
                              bg: "transparent",
                              borderRadius: "$sm",
                              border: "1px solid $gray300",
                            }}
                          >
                            <Card.Body css={{ p: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    flex: 1,
                                    overflow: "hidden",
                                  }}
                                >
                                  <Text
                                    css={{
                                      color: "$gray500",
                                      fontSize: "$xs",
                                      minWidth: "18px",
                                      textAlign: "left",
                                    }}
                                  >
                                    {i + 1}.
                                  </Text>
                                  <Text
                                    b
                                    css={{
                                      fontSize: "$sm",
                                      lineHeight: "$sm",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {itemDoc.category} · {itemDoc.name}
                                  </Text>
                                </div>
                                <Badge
                                  color="success"
                                  variant="flat"
                                  size="sm"
                                  css={{
                                    minWidth: "24px",
                                    height: "24px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "$xs",
                                    fontWeight: "bold",
                                    borderRadius: "6px",
                                  }}
                                >
                                  {itemDoc?.qty}
                                </Badge>
                              </div>
                            </Card.Body>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid.Container>

                {/* Print Layout (Hidden) */}
                <div style={{ display: "none" }}>
                  <div ref={contentRef} style={{ padding: "24px" }}>
                    <Text h3 css={{ mb: "$10" }}>
                      Marvans Second Hand Inventory
                    </Text>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      {Object.keys(data)
                        .sort((a, b) => data[b].qty - data[a].qty)
                        .map((doc, i) => {
                          const itemDoc = data[doc];
                          return (
                            <Text key={i} b>
                              {i + 1}. {itemDoc.category} · {itemDoc.name} - {itemDoc.qty}
                            </Text>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </>
            )
          )}
        </Container>
      </div>
    </motion.div>
  );
};

export default SecondHandInventory;
